import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db, collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp, getDocs, where, campos } from './firestore-compat';
import { getCollectionName } from './firebase';
import { uploadArquivo, ArquivoImg, abrirArquivo } from './arquivos';

// ─── Paleta ────────────────────────────────────────────────────────────────
const C = {
  bg:'#f6f7f9', white:'#ffffff', border:'#e7e9ee',
  text:'#1a1d23', sub:'#6b7280', muted:'#9ca3af',
  atendimento:'#f59e0b', hospitalizado:'#ef4444', alta:'#22c55e',
  green:'#1e9e5a', greenBg:'#e7f6ee',
  amber:'#b7861a', amberBg:'#fdf4dd',
  red:'#d64545',   redBg:'#fdeaea',
  blue:'#4f5ef7',  blueBg:'#eef0ff',
  purple:'#7c3aed',purpleBg:'#f5f3ff',
};

// ─── Constantes ──────────────────────────────────────────────────────────────
// Normaliza nomes legados do banco para o nome atual
const nf = v => v === 'Homesenior' ? 'Vitalcare' : (v || '');

const FILIAIS = ['Homebaby','Vitalcare'];
const PROGRAMAS = ['ID','AD','Pontual','Terapias'];
const ORIGENS_LESAO = ['Interna','Externa'];
const PERIODICIDADES = ['Diária','Semanal','Quinzenal','28 dias','Mensal'];
const META_OPTS = ['em andamento','atingida','não atingida'];
const PERIODO_DIAS = { 'Diária':1,'Semanal':7,'Quinzenal':14,'28 dias':28,'Mensal':30 };

const TIPOS_LESAO = [
  'Úlcera por pressão','Úlcera venosa','Úlcera arterial','Úlcera neuropática',
  'Ferida cirúrgica','Queimadura','Ferida traumática','Deiscência','Outro',
];
const ESTADIOS = ['I','II','III','IV','Não classificável','Suspeita lesão tissular profunda'];
const LOCALIZACOES = [
  'Sacral','Calcâneo','Trocânter','Isquio','Occipital','Cotovelo','Maléolo',
  'Pé','MMII','Abdome','Dorso','Outro',
];
const COBERTURAS = [
  'AGE','Hidrogel','Alginato de cálcio','PHMB','Prata nanoparticulada','Carvão ativado',
  'Silicone','Espuma','Papaína','Colagenase','Outro',
];
const EVOLUCAO_STATUS = ['Melhora','Estabilidade','Piora','Cicatrização parcial'];
const MOTIVOS_ALTA_HOMECARE = ['Mudança de homecare','Óbito','Outro'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function calcProximaTroca(ultimaData, periodicidade) {
  if (!ultimaData || !periodicidade) return null;
  const d = new Date(ultimaData + 'T12:00:00');
  d.setDate(d.getDate() + (PERIODO_DIAS[periodicidade] || 7));
  return d.toISOString().slice(0, 10);
}

function diffDias(dateISO) {
  if (!dateISO) return null;
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const alvo = new Date(dateISO + 'T00:00:00');
  return Math.round((alvo - hoje) / 86400000);
}

function statusTroca(proxima, hospitalizado) {
  if (hospitalizado) return { label:'Hospitalizado', color:C.red, bg:C.redBg };
  if (!proxima) return { label:'Sem troca agendada', color:C.muted, bg:'#f1f5f9' };
  const d = diffDias(proxima);
  if (d < 0)  return { label:`Atrasado ${Math.abs(d)}d`, color:C.red, bg:C.redBg };
  if (d === 0) return { label:'Trocar hoje', color:C.amber, bg:C.amberBg };
  if (d <= 2)  return { label:`Em ${d}d`, color:C.amber, bg:C.amberBg };
  return { label:`Em ${d}d`, color:C.green, bg:C.greenBg };
}

function calcIdade(dn) {
  if (!dn) return '';
  const d = new Date(dn + 'T12:00:00');
  const hoje = new Date();
  let age = hoje.getFullYear() - d.getFullYear();
  const m = hoje.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < d.getDate())) age--;
  return age;
}

function situacaoColor(s) {
  if (s === 'Em atendimento') return C.atendimento;
  if (s === 'Hospitalizado')  return C.hospitalizado;
  if (s === 'Alta da comissão') return C.alta;
  if (s === 'Alta do Homecare') return C.purple;
  return C.muted;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const [y,m,d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function hoje() { return new Date().toISOString().slice(0,10); }

// ─── Componentes base ─────────────────────────────────────────────────────────
function Pill({ label, color, bg, small }) {
  return (
    <span style={{
      display:'inline-block', padding: small ? '2px 8px' : '3px 10px',
      borderRadius:20, fontSize: small ? '0.68rem' : '0.72rem',
      fontWeight:700, color, background:bg, whiteSpace:'nowrap',
    }}>{label}</span>
  );
}

function Avatar({ name, size=32 }) {
  const initials = (name||'?').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase();
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%', background:C.blue, color:'#fff',
      display:'flex',alignItems:'center',justifyContent:'center',
      fontSize:size*0.38, fontWeight:700, flexShrink:0,
    }}>{initials}</div>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, color:C.sub, marginBottom:4 }}>
        {label}{required && <span style={{color:C.red}}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputSt = {
  width:'100%', padding:'7px 10px', border:`1px solid ${C.border}`,
  borderRadius:6, fontSize:'0.88rem', color:C.text, background:C.white,
  boxSizing:'border-box',
};

function Inp({ value, onChange, type='text', placeholder, min, max }) {
  return <input type={type} value={value||''} onChange={e=>onChange(e.target.value)}
    placeholder={placeholder} min={min} max={max} style={inputSt} />;
}

function AutocompleteNome({ value, onChange, suggestions }) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const v = (value || '').toLowerCase();
    if (v.length < 2) return [];
    return suggestions.filter(s => s.toLowerCase().includes(v)).slice(0, 10);
  }, [value, suggestions]);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={value || ''}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Nome do paciente"
        style={inputSt}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: `1px solid ${C.border}`, borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto',
        }}>
          {filtered.map((s, i) => (
            <div key={i}
              onMouseDown={() => { onChange(s); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem',
                borderBottom: `1px solid ${C.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value||''} onChange={e=>onChange(e.target.value)} style={inputSt}>
      {placeholder && <option value="">{placeholder}</option>}
      {(options||[]).map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Txt({ value, onChange, rows=3, placeholder }) {
  return <textarea value={value||''} onChange={e=>onChange(e.target.value)}
    rows={rows} placeholder={placeholder}
    style={{ ...inputSt, resize:'vertical', lineHeight:1.5 }} />;
}

function Btn({ children, onClick, color=C.blue, disabled, small, outline }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '5px 12px' : '8px 18px',
      background: outline ? 'transparent' : color,
      color: outline ? color : '#fff',
      border: `1.5px solid ${color}`,
      borderRadius:7, fontSize: small ? '0.78rem' : '0.85rem',
      fontWeight:600, cursor:disabled?'not-allowed':'pointer',
      opacity:disabled?.5:1, whiteSpace:'nowrap',
    }}>{children}</button>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, width=540 }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{
        background:C.white, borderRadius:12, width:'100%', maxWidth:width,
        maxHeight:'90vh', overflow:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'18px 22px 14px', borderBottom:`1px solid ${C.border}`,
          position:'sticky', top:0, background:C.white, zIndex:1,
        }}>
          <span style={{ fontWeight:700, fontSize:'1rem', color:C.text }}>{title}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.muted }}>✕</button>
        </div>
        <div style={{ padding:'18px 22px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Galeria de fotos ─────────────────────────────────────────────────────────
function PhotoGallery({ fotos }) {
  const [ampliada, setAmpliada] = useState(null);
  if (!fotos || fotos.length === 0) return null;
  return (
    <>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
        {fotos.map((f, i) => (
          <ArquivoImg
            key={i} src={f.url} alt={f.nome||`foto ${i+1}`}
            onClick={() => setAmpliada(f.url)}
            style={{
              width:64, height:64, objectFit:'cover', borderRadius:6,
              border:`1.5px solid ${C.border}`, cursor:'pointer',
              transition:'transform 0.15s',
            }}
            onMouseEnter={e=>e.target.style.transform='scale(1.08)'}
            onMouseLeave={e=>e.target.style.transform='scale(1)'}
          />
        ))}
      </div>
      {ampliada && (
        <div
          onClick={() => setAmpliada(null)}
          style={{
            position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000,
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out',
          }}
        >
          <ArquivoImg src={ampliada} alt="ampliada" style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:10 }} />
        </div>
      )}
    </>
  );
}

// ─── Componente de upload de fotos ────────────────────────────────────────────
function FotoUploader({ fotos, onChange }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [erro, setErro] = useState('');

  const handleFiles = async (files) => {
    const allowed = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (allowed.length === 0) { setErro('Selecione apenas imagens (JPG, PNG, WEBP).'); return; }
    setErro('');
    setUploading(true);
    try {
      const uploads = await Promise.all(
        allowed.map(f => uploadArquivo(f, 'feridas'))
      );
      onChange([...(fotos||[]), ...uploads]);
    } catch(e) {
      setErro('Erro no upload: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const remover = (idx) => onChange((fotos||[]).filter((_,i) => i !== idx));

  return (
    <div>
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); }}
        onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        style={{
          border:`2px dashed ${C.border}`, borderRadius:8, padding:'14px 12px',
          textAlign:'center', cursor: uploading ? 'not-allowed' : 'pointer',
          background: uploading ? '#f8fafc' : C.white, transition:'border-color 0.2s',
          fontSize:'0.82rem', color:C.sub,
        }}
        onMouseEnter={e => !uploading && (e.currentTarget.style.borderColor = C.blue)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        {uploading
          ? '⏳ Enviando fotos...'
          : '📷 Clique ou arraste fotos aqui (JPG, PNG, WEBP)'}
      </div>
      <input
        ref={inputRef} type="file" accept="image/*" multiple style={{ display:'none' }}
        onChange={e => handleFiles(e.target.files)}
      />
      {erro && <div style={{ color:C.red, fontSize:'0.75rem', marginTop:4 }}>{erro}</div>}
      {fotos && fotos.length > 0 && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
          {fotos.map((f, i) => (
            <div key={i} style={{ position:'relative' }}>
              <ArquivoImg src={f.url} alt={f.nome} style={{
                width:72, height:72, objectFit:'cover', borderRadius:6,
                border:`1.5px solid ${C.border}`,
              }} />
              <button
                onClick={() => remover(i)}
                style={{
                  position:'absolute', top:-6, right:-6, width:18, height:18,
                  borderRadius:'50%', background:C.red, color:'#fff',
                  border:'none', cursor:'pointer', fontSize:10, lineHeight:'18px',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}
              >✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Timeline de evoluções ────────────────────────────────────────────────────
function EvolutionTimeline({ evolucoes, onDelete }) {
  if (!evolucoes || evolucoes.length === 0) {
    return <div style={{ color:C.muted, fontSize:'0.8rem', padding:'8px 0' }}>Nenhuma evolução registrada.</div>;
  }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {evolucoes.map((ev, i) => (
        <div key={ev.id||i} style={{
          borderLeft:`3px solid ${i===0?C.blue:C.border}`, paddingLeft:12,
          paddingBottom: i<evolucoes.length-1 ? 8 : 0,
        }}>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:2, justifyContent:'space-between' }}>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontWeight:700, fontSize:'0.78rem', color:i===0?C.blue:C.sub }}>{fmtDate(ev.data)}</span>
              {ev.status_evolucao && <Pill label={ev.status_evolucao} color={C.purple} bg={C.purpleBg} small />}
            </div>
            {onDelete && (
              <button onClick={()=>onDelete(ev)} title="Excluir evolução"
                style={{ background:'none', border:'none', color:C.red, cursor:'pointer', fontSize:'0.75rem', padding:2 }}>
                🗑
              </button>
            )}
          </div>
          {ev.cobertura_utilizada && (
            <div style={{ fontSize:'0.78rem', color:C.sub }}>Cobertura: <strong>{ev.cobertura_utilizada}</strong></div>
          )}
          {ev.observacoes && (
            <div style={{ fontSize:'0.78rem', color:C.text, marginTop:2 }}>{ev.observacoes}</div>
          )}
          {ev.profissional && (
            <div style={{ fontSize:'0.72rem', color:C.muted, marginTop:2 }}>Por: {ev.profissional}</div>
          )}
          <PhotoGallery fotos={ev.fotos} />
        </div>
      ))}
    </div>
  );
}

// ─── Card de lesão ────────────────────────────────────────────────────────────
function WoundCard({ lesao, onNovaEvolucao, onCicatrizacao, onReabrir, onDelete, onDeleteEvolucao }) {
  const [showEv, setShowEv] = useState(false);
  const st = statusTroca(lesao.proxima_calculada, false);
  const cicatrizada = !!lesao.data_cicatrizacao;

  return (
    <div style={{
      background:C.white, border:`1px solid ${C.border}`,
      borderLeft:`4px solid ${cicatrizada ? C.alta : C.blue}`,
      borderRadius:8, padding:'12px 14px', position:'relative',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, flexWrap:'wrap' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:'0.88rem', color:C.text }}>
            {lesao.tipo_lesao || 'Lesão'}{lesao.localizacao ? ` — ${lesao.localizacao}` : ''}
            {lesao.estadio ? <span style={{ color:C.sub, fontWeight:400 }}> (Estádio {lesao.estadio})</span> : ''}
          </div>
          {lesao.descricao_local && (
            <div style={{ fontSize:'0.78rem', color:C.sub, marginTop:2, fontStyle:'italic' }}>{lesao.descricao_local}</div>
          )}
          <div style={{ fontSize:'0.75rem', color:C.sub, marginTop:2 }}>
            {lesao.data_primeira_avaliacao ? `1ª avaliação: ${fmtDate(lesao.data_primeira_avaliacao)}` : ''}
            {lesao.periodicidade_troca ? ` · Troca: ${lesao.periodicidade_troca}` : ''}
            {lesao.numero_notificacao ? ` · Notif. ${lesao.numero_notificacao}` : ''}
          </div>
          {lesao.meta_programada && (
            <div style={{ fontSize:'0.75rem', color:C.purple }}>Meta: {lesao.meta_programada}</div>
          )}
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {!cicatrizada && <Pill label={st.label} color={st.color} bg={st.bg} small />}
          {cicatrizada && <Pill label={`Cicatrizada ${fmtDate(lesao.data_cicatrizacao)}`} color={C.green} bg={C.greenBg} small />}
        </div>
      </div>

      {/* Thumbnail da foto mais recente */}
      {(() => {
        const ultimaComFoto = (lesao.evolucoes||[]).find(e => e.fotos && e.fotos.length > 0);
        const fotoLesao = lesao.fotos?.[0];
        const foto = ultimaComFoto?.fotos?.[0] || fotoLesao;
        if (!foto) return null;
        return (
          <div style={{ marginTop:8 }}>
            <ArquivoImg src={foto.url} alt="última foto" style={{
              width:80, height:80, objectFit:'cover', borderRadius:6,
              border:`1.5px solid ${C.border}`, cursor:'pointer',
            }}
            onClick={() => abrirArquivo(foto.url)}
            title={`Última foto · ${ultimaComFoto ? fmtDate(ultimaComFoto.data) : 'avaliação inicial'}`}
            />
            <div style={{ fontSize:'0.68rem', color:C.muted, marginTop:2 }}>
              {ultimaComFoto ? `Foto de ${fmtDate(ultimaComFoto.data)}` : 'Foto inicial'}
            </div>
          </div>
        );
      })()}
      {lesao.cobertura_atual && (
        <div style={{ fontSize:'0.78rem', color:C.sub, marginTop:6 }}>
          Cobertura atual: <strong style={{color:C.text}}>{lesao.cobertura_atual}</strong>
        </div>
      )}
      {lesao.dimensoes && (
        <div style={{ fontSize:'0.78rem', color:C.sub }}>Dimensões: {lesao.dimensoes}</div>
      )}
      {lesao.observacoes && (
        <div style={{ fontSize:'0.78rem', color:C.text, marginTop:4, fontStyle:'italic' }}>{lesao.observacoes}</div>
      )}

      <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
        {!cicatrizada && (
          <>
            <Btn small outline color={C.blue} onClick={()=>onNovaEvolucao(lesao)}>+ Evolução</Btn>
            <Btn small outline color={C.green} onClick={()=>onCicatrizacao(lesao)}>✓ Cicatrização</Btn>
          </>
        )}
        {cicatrizada && (
          <Btn small outline color={C.amber} onClick={()=>onReabrir(lesao)}>↩ Reabrir</Btn>
        )}
        <Btn small outline color={C.muted} onClick={()=>setShowEv(v=>!v)}>
          {showEv ? 'Ocultar evoluções' : `Ver evoluções (${lesao.evolucoes?.length||0})`}
        </Btn>
        <Btn small outline color={C.red} onClick={()=>onDelete(lesao)}>🗑 Excluir lesão</Btn>
      </div>

      {showEv && (
        <div style={{ marginTop:12, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
          <EvolutionTimeline evolucoes={lesao.evolucoes||[]} onDelete={onDeleteEvolucao} />
        </div>
      )}
    </div>
  );
}

// ─── Painel expandido do paciente ─────────────────────────────────────────────
function PatientExpandedPanel({ pac, onAction }) {
  const situacao = pac.situacao || 'Em atendimento';
  return (
    <tr>
      <td colSpan={99} style={{ padding:0, background:C.bg }}>
        <div style={{ padding:'16px 24px 20px', display:'flex', gap:20, flexWrap:'wrap' }}>

          {/* Coluna esquerda: dados do paciente */}
          <div style={{
            background:C.white, border:`1px solid ${C.border}`, borderLeft:`4px solid ${situacaoColor(situacao)}`,
            borderRadius:8, padding:'14px 16px', minWidth:220, maxWidth:280, flex:'0 0 240px',
          }}>
            <div style={{ fontWeight:800, fontSize:'0.95rem', color:C.text, marginBottom:8 }}>{pac.nome}</div>
            <div style={{ fontSize:'0.78rem', color:C.sub, lineHeight:1.8 }}>
              {pac.data_nascimento && <div>DN: <strong>{fmtDate(pac.data_nascimento)}</strong>{calcIdade(pac.data_nascimento) ? ` (${calcIdade(pac.data_nascimento)} anos)` : ''}</div>}
              {pac.filial && <div>Filial: <strong>{nf(pac.filial)}</strong></div>}
              {pac.programa_atual && <div>Programa: <strong>{nf(pac.programa_atual)}</strong></div>}
              {pac.operadora && <div>Operadora: <strong>{pac.operadora}</strong></div>}
              {pac.data_implantacao && <div>Implantação: <strong>{fmtDate(pac.data_implantacao)}</strong></div>}
              {pac.diagnostico_base && <div>Diagnóstico: <strong>{pac.diagnostico_base}</strong></div>}
              {pac.profissional_avaliacao && <div>Avaliador: <strong>{pac.profissional_avaliacao}</strong></div>}
              {pac.origem_lesao && <div>Origem lesão: <strong>{pac.origem_lesao}</strong></div>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:12 }}>
              <Btn small color={C.blue} onClick={()=>onAction('new_wound', pac)}>+ Nova lesão</Btn>
              {situacao === 'Em atendimento' && (
                <Btn small color={C.red} onClick={()=>onAction('hospitalize', pac)}>Hospitalizar</Btn>
              )}
              {situacao === 'Hospitalizado' && (
                <Btn small color={C.amber} onClick={()=>onAction('desospitalizar', pac)}>Desospitalizar</Btn>
              )}
              {situacao !== 'Alta da comissão' && situacao !== 'Alta do Homecare' && (
                <Btn small color={C.green} onClick={()=>onAction('alta_comissao', pac)}>Alta da comissão</Btn>
              )}
              {situacao !== 'Alta da comissão' && situacao !== 'Alta do Homecare' && (
                <Btn small color={C.purple} onClick={()=>onAction('alta_homecare', pac)}>Alta do Homecare</Btn>
              )}
              {(situacao === 'Alta da comissão' || situacao === 'Alta do Homecare') && (
                <Btn small color={C.amber} onClick={()=>onAction('reabrir_pac', pac)}>↩ Reativar</Btn>
              )}
              <Btn small outline color={C.red} onClick={()=>onAction('delete_patient', pac)}>🗑 Excluir paciente</Btn>
            </div>
          </div>

          {/* Coluna direita: lesões */}
          <div style={{ flex:1, minWidth:300 }}>
            {(!pac.lesoes || pac.lesoes.length === 0) ? (
              <div style={{ color:C.muted, fontSize:'0.85rem', padding:'20px 0' }}>
                Nenhuma lesão cadastrada. Clique em "+ Nova lesão" para começar.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {pac.lesoes.map(l => (
                  <WoundCard
                    key={l.id}
                    lesao={l}
                    onNovaEvolucao={lesao=>onAction('evolution', { pac, lesao })}
                    onCicatrizacao={lesao=>onAction('cicatrizacao', { pac, lesao })}
                    onReabrir={lesao=>onAction('reabrir_lesao', { pac, lesao })}
                    onDelete={lesao=>onAction('delete_lesao', lesao)}
                    onDeleteEvolucao={ev=>onAction('delete_evolucao', ev)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ─── Linha do paciente ────────────────────────────────────────────────────────
function PatientRow({ pac, expanded, onToggle, onAction }) {
  const situacao = pac.situacao || 'Em atendimento';
  const acorColor = situacaoColor(situacao);
  const proxSt = statusTroca(pac.proxima_troca_urgente, situacao==='Hospitalizado');
  const totalLesoes = pac.lesoes?.length || 0;
  const ativas = pac.lesoes?.filter(l=>!l.data_cicatrizacao).length || 0;

  return (
    <tr style={{
      cursor:'pointer', borderBottom:`1px solid ${C.border}`,
      background: expanded ? '#f0f3ff' : C.white,
      transition:'background 0.15s',
    }} onClick={onToggle}>
      <td style={{ padding:'11px 8px 11px 16px', width:32 }}>
        <span style={{ fontSize:12, color:C.muted }}>{expanded ? '▼' : '▶'}</span>
      </td>
      <td style={{ padding:'11px 8px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:3, height:36, borderRadius:2, background:acorColor, flexShrink:0 }} />
          <Avatar name={pac.nome} />
          <div>
            <div style={{ fontWeight:700, fontSize:'0.9rem', color:C.text }}>{pac.nome}</div>
            <div style={{ fontSize:'0.75rem', color:C.sub }}>
              {calcIdade(pac.data_nascimento) ? `${calcIdade(pac.data_nascimento)} anos · ` : ''}{pac.operadora||''}{pac.filial ? ` · ${nf(pac.filial)}` : ''}
            </div>
          </div>
        </div>
      </td>
      <td style={{ padding:'11px 8px', fontSize:'0.82rem', color:C.sub }}>{nf(pac.filial)||'—'}</td>
      <td style={{ padding:'11px 8px' }}>
        <Pill label={situacao} color={acorColor} bg={`${acorColor}18`} small />
      </td>
      <td style={{ padding:'11px 8px', fontSize:'0.82rem', color:C.sub, textAlign:'center' }}>
        <strong style={{color:C.text}}>{totalLesoes}</strong> / <span style={{color:C.green}}>{ativas} ativa{ativas!==1?'s':''}</span>
      </td>
      <td style={{ padding:'11px 8px' }}>
        <Pill label={proxSt.label} color={proxSt.color} bg={proxSt.bg} small />
      </td>
      <td style={{ padding:'11px 8px' }}>
        <Pill
          label={pac.meta_consolidada||'em andamento'}
          color={pac.meta_consolidada==='atingida'?C.green:pac.meta_consolidada==='não atingida'?C.red:C.amber}
          bg={pac.meta_consolidada==='atingida'?C.greenBg:pac.meta_consolidada==='não atingida'?C.redBg:C.amberBg}
          small
        />
      </td>
      <td style={{ padding:'11px 16px 11px 8px' }}>
        <div style={{ display:'flex', gap:6 }}>
          <Btn small outline color={C.blue} onClick={e=>{e.stopPropagation();onAction('edit_patient',pac)}}>Editar</Btn>
          <Btn small outline color={C.red} onClick={e=>{e.stopPropagation();onAction('delete_patient',pac)}}>🗑</Btn>
        </div>
      </td>
    </tr>
  );
}

// ─── Painel "Trocas de hoje" ──────────────────────────────────────────────────
function TrocasHojePanel({ pacientes, onClose }) {
  const trocas = useMemo(() => {
    const list = [];
    pacientes.forEach(p => {
      (p.lesoes||[]).forEach(l => {
        if (!l.data_cicatrizacao && l.proxima_calculada) {
          const d = diffDias(l.proxima_calculada);
          if (d !== null && d <= 0) {
            list.push({ pac:p, lesao:l, diff:d });
          }
        }
      });
    });
    return list.sort((a,b)=>a.diff-b.diff);
  }, [pacientes]);

  return (
    <Modal title={`🩹 Trocas de hoje / atrasadas (${trocas.length})`} onClose={onClose} width={560}>
      {trocas.length === 0 ? (
        <div style={{ color:C.green, fontWeight:600, textAlign:'center', padding:'24px 0' }}>
          Nenhuma troca pendente hoje!
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {trocas.map((t,i) => {
            const st = statusTroca(t.lesao.proxima_calculada, false);
            return (
              <div key={i} style={{
                background:C.bg, borderRadius:8, padding:'10px 14px',
                borderLeft:`4px solid ${st.color}`,
              }}>
                <div style={{ fontWeight:700, fontSize:'0.88rem', color:C.text }}>{t.pac.nome}</div>
                <div style={{ fontSize:'0.78rem', color:C.sub }}>
                  {t.lesao.tipo_lesao}{t.lesao.localizacao ? ` — ${t.lesao.localizacao}` : ''}
                </div>
                <Pill label={st.label} color={st.color} bg={st.bg} small />
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function GestaoFeridas() {
  const [pacientes,   setPacientes]   = useState([]);
  const [lesoes,      setLesoes]      = useState([]);
  const [evolucoes,   setEvolucoes]   = useState([]);
  const [abaAtiva,    setAbaAtiva]    = useState('atendimento');
  const [expanded,    setExpanded]    = useState(null);
  const [modal,       setModal]       = useState(null);
  const [form,        setForm]        = useState({});
  const [saving,      setSaving]      = useState(false);
  const [showTrocas,  setShowTrocas]  = useState(false);
  const [busca,       setBusca]       = useState('');
  const [delItem,     setDelItem]     = useState(null); // { tipo:'paciente'|'lesao'|'evolucao', id, nome }

  const colPac = getCollectionName('lesoes_pacientes');
  const colLes = getCollectionName('lesoes_lesoes');
  const colEvo = getCollectionName('lesoes_evolucoes');
  const [nomesSugeridos, setNomesSugeridos] = useState([]);

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, colPac), orderBy('createdAt','desc')), snap => {
      setPacientes(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    });
    const u2 = onSnapshot(query(collection(db, colLes), orderBy('createdAt','asc')), snap => {
      setLesoes(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    });
    const u3 = onSnapshot(query(collection(db, colEvo), orderBy('data','desc')), snap => {
      setEvolucoes(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    });
    return () => { u1(); u2(); u3(); };
  }, [colPac, colLes, colEvo]);

  useEffect(() => {
    let canceled = false;
    Promise.all([
      getDocs(query(collection(db, getCollectionName('captacao_pacientes')), where('status', '==', 'Implantado'), campos('nome'))),
      getDocs(collection(db, getCollectionName('escalas_pacientes'))),
    ]).then(([capSnap, escSnap]) => {
      if (canceled) return;
      const nomes = new Set();
      capSnap.docs.forEach(d => { const n = d.data().nome; if (n) nomes.add(n); });
      escSnap.docs.forEach(d => { const n = d.data().nome; if (n) nomes.add(n); });
      setNomesSugeridos([...nomes].sort((a, b) => a.localeCompare(b)));
    }).catch(() => {});
    return () => { canceled = true; };
  }, []);

  // Join das três coleções
  const pacientesComDados = useMemo(() => {
    return pacientes.map(p => {
      const minhasLesoes = lesoes.filter(l => l.pacienteId === p.id).map(l => {
        const evs = evolucoes.filter(e => e.lesaoId === l.id).sort((a,b)=>(b.data||'').localeCompare(a.data||''));
        const ultimaData = evs[0]?.data || l.data_primeira_avaliacao;
        const proxima = calcProximaTroca(ultimaData, l.periodicidade_troca);
        return { ...l, evolucoes:evs, proxima_calculada: l.data_cicatrizacao ? null : proxima };
      });
      const meta_consolidada = minhasLesoes.length===0 ? 'em andamento'
        : minhasLesoes.every(l=>l.meta_status==='atingida') ? 'atingida'
        : minhasLesoes.some(l=>l.meta_status==='não atingida') ? 'não atingida'
        : 'em andamento';
      const proximas = minhasLesoes
        .filter(l=>!l.data_cicatrizacao && l.proxima_calculada)
        .map(l=>l.proxima_calculada).sort();
      return { ...p, lesoes:minhasLesoes, meta_consolidada, proxima_troca_urgente:proximas[0]||null };
    });
  }, [pacientes, lesoes, evolucoes]);

  // Filtros por aba
  const pacientesFiltrados = useMemo(() => {
    const mapa = { atendimento:'Em atendimento', hospitalizado:'Hospitalizado', alta:'Alta da comissão', alta_homecare:'Alta do Homecare' };
    let list = pacientesComDados.filter(p => (p.situacao||'Em atendimento') === mapa[abaAtiva]);
    if (busca.trim()) {
      const b = busca.toLowerCase();
      list = list.filter(p => (p.nome||'').toLowerCase().includes(b) || (p.operadora||'').toLowerCase().includes(b));
    }
    if (abaAtiva === 'atendimento') {
      list = list.sort((a,b) => {
        const da = a.proxima_troca_urgente||'9999-12-31';
        const db_ = b.proxima_troca_urgente||'9999-12-31';
        return da.localeCompare(db_);
      });
    }
    return list;
  }, [pacientesComDados, abaAtiva, busca]);

  const trocasHojeCount = useMemo(() => {
    let n = 0;
    pacientesComDados.filter(p=>(p.situacao||'Em atendimento')==='Em atendimento').forEach(p => {
      (p.lesoes||[]).forEach(l => {
        if (!l.data_cicatrizacao && l.proxima_calculada) {
          const d = diffDias(l.proxima_calculada);
          if (d !== null && d <= 0) n++;
        }
      });
    });
    return n;
  }, [pacientesComDados]);

  // Abrir modal
  const openModal = useCallback((tipo, ctx) => {
    setModal(tipo);
    if (tipo === 'new_patient') {
      setForm({ situacao:'Em atendimento', data_implantacao: hoje() });
    } else if (tipo === 'edit_patient') {
      setForm({ ...ctx });
    } else if (tipo === 'new_wound') {
      setForm({ pacienteId: ctx.id, data_primeira_avaliacao: hoje(), meta_status:'em andamento' });
    } else if (tipo === 'evolution') {
      setForm({ lesaoId: ctx.lesao.id, pacienteId: ctx.pac.id, data: hoje(), cobertura_utilizada: ctx.lesao.cobertura_atual||'' });
    } else if (tipo === 'hospitalize') {
      setForm({ pacienteId: ctx.id, data_hospitalizacao: hoje() });
    } else if (tipo === 'desospitalizar') {
      setForm({ pacienteId: ctx.id });
    } else if (tipo === 'cicatrizacao') {
      setForm({ lesaoId: ctx.lesao.id, data_cicatrizacao: hoje() });
    } else if (tipo === 'alta_comissao') {
      setForm({ pacienteId: ctx.id, data_alta: hoje() });
    } else if (tipo === 'alta_homecare') {
      setForm({ pacienteId: ctx.id, data_alta: hoje(), motivo_alta_homecare: '' });
    } else if (tipo === 'reabrir_pac') {
      setForm({ pacienteId: ctx.id });
    } else if (tipo === 'reabrir_lesao') {
      setForm({ lesaoId: ctx.lesao.id });
    }
  }, []);

  // Roteia ações de exclusão para o modal de confirmação; demais ações abrem o modal de formulário
  const handleAction = useCallback((tipo, ctx) => {
    if (tipo === 'delete_patient') setDelItem({ tipo:'paciente', id: ctx.id, nome: ctx.nome });
    else if (tipo === 'delete_lesao') setDelItem({ tipo:'lesao', id: ctx.id, nome: ctx.tipo_lesao || 'lesão' });
    else if (tipo === 'delete_evolucao') setDelItem({ tipo:'evolucao', id: ctx.id, nome: fmtDate(ctx.data) });
    else openModal(tipo, ctx);
  }, [openModal]);

  // Excluir (com cascata: paciente → lesões → evoluções; lesão → evoluções)
  const confirmarExclusao = useCallback(async () => {
    if (!delItem) return;
    setSaving(true);
    try {
      if (delItem.tipo === 'paciente') {
        const minhasLesoes = lesoes.filter(l => l.pacienteId === delItem.id);
        const idsLesoes = minhasLesoes.map(l => l.id);
        const minhasEvolucoes = evolucoes.filter(e => idsLesoes.includes(e.lesaoId));
        await Promise.all(minhasEvolucoes.map(e => deleteDoc(doc(db, colEvo, e.id))));
        await Promise.all(minhasLesoes.map(l => deleteDoc(doc(db, colLes, l.id))));
        await deleteDoc(doc(db, colPac, delItem.id));
        setExpanded(v => v === delItem.id ? null : v);
      } else if (delItem.tipo === 'lesao') {
        const minhasEvolucoes = evolucoes.filter(e => e.lesaoId === delItem.id);
        await Promise.all(minhasEvolucoes.map(e => deleteDoc(doc(db, colEvo, e.id))));
        await deleteDoc(doc(db, colLes, delItem.id));
      } else if (delItem.tipo === 'evolucao') {
        await deleteDoc(doc(db, colEvo, delItem.id));
      }
      setDelItem(null);
    } catch(e) {
      alert('Erro ao excluir: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [delItem, lesoes, evolucoes, colPac, colLes, colEvo]);

  // Salvar
  const save = useCallback(async () => {
    setSaving(true);
    try {
      if (modal === 'new_patient') {
        await addDoc(collection(db, colPac), { ...form, createdAt: serverTimestamp() });
      } else if (modal === 'edit_patient') {
        const { id, lesoes:_l, meta_consolidada:_m, proxima_troca_urgente:_p, ...rest } = form;
        await updateDoc(doc(db, colPac, id), { ...rest });
      } else if (modal === 'new_wound') {
        await addDoc(collection(db, colLes), { ...form, createdAt: serverTimestamp() });
      } else if (modal === 'evolution') {
        await addDoc(collection(db, colEvo), { ...form, createdAt: serverTimestamp() });
        if (form.cobertura_utilizada) {
          await updateDoc(doc(db, colLes, form.lesaoId), { cobertura_atual: form.cobertura_utilizada });
        }
      } else if (modal === 'hospitalize') {
        await updateDoc(doc(db, colPac, form.pacienteId), {
          situacao:'Hospitalizado', data_hospitalizacao: form.data_hospitalizacao,
        });
      } else if (modal === 'desospitalizar') {
        await updateDoc(doc(db, colPac, form.pacienteId), {
          situacao:'Em atendimento', data_hospitalizacao: null,
        });
      } else if (modal === 'cicatrizacao') {
        await updateDoc(doc(db, colLes, form.lesaoId), {
          data_cicatrizacao: form.data_cicatrizacao, meta_status:'atingida',
        });
      } else if (modal === 'alta_comissao') {
        await updateDoc(doc(db, colPac, form.pacienteId), {
          situacao:'Alta da comissão', data_alta: form.data_alta,
        });
      } else if (modal === 'alta_homecare') {
        await updateDoc(doc(db, colPac, form.pacienteId), {
          situacao:'Alta do Homecare', data_alta: form.data_alta, motivo_alta_homecare: form.motivo_alta_homecare || '',
        });
      } else if (modal === 'reabrir_pac') {
        await updateDoc(doc(db, colPac, form.pacienteId), {
          situacao:'Em atendimento', data_alta: null, motivo_alta_homecare: null,
        });
      } else if (modal === 'reabrir_lesao') {
        await updateDoc(doc(db, colLes, form.lesaoId), {
          data_cicatrizacao: null, meta_status:'em andamento',
        });
      }
      setModal(null);
      setForm({});
    } catch(e) {
      alert('Erro ao salvar: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [modal, form, colPac, colLes, colEvo]);

  const ABAS = [
    { key:'atendimento', label:'Em atendimento', color:C.atendimento },
    { key:'hospitalizado', label:'Hospitalizados', color:C.hospitalizado },
    { key:'alta', label:'Alta da comissão', color:C.alta },
    { key:'alta_homecare', label:'Alta do Homecare', color:C.purple },
  ];

  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:'24px 20px', fontFamily:'Inter,system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ margin:0, fontSize:'1.35rem', fontWeight:800, color:C.text }}>🩹 Gestão de Feridas</h1>
          <div style={{ fontSize:'0.8rem', color:C.sub, marginTop:2 }}>
            {pacientesComDados.length} paciente{pacientesComDados.length!==1?'s':''} · {lesoes.filter(l=>!l.data_cicatrizacao).length} lesões ativas
          </div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {trocasHojeCount > 0 && (
            <button onClick={()=>setShowTrocas(true)} style={{
              background:C.redBg, color:C.red, border:`1.5px solid ${C.red}`,
              borderRadius:8, padding:'7px 14px', fontWeight:700, fontSize:'0.83rem',
              cursor:'pointer', display:'flex', alignItems:'center', gap:6,
            }}>
              <span>🩹 Trocas de hoje</span>
              <span style={{
                background:C.red, color:'#fff', borderRadius:20,
                padding:'0px 7px', fontSize:'0.75rem', fontWeight:800,
              }}>{trocasHojeCount}</span>
            </button>
          )}
          <input
            placeholder="Buscar paciente..."
            value={busca}
            onChange={e=>setBusca(e.target.value)}
            style={{ ...inputSt, width:200, padding:'7px 12px' }}
          />
          <Btn color={C.blue} onClick={()=>openModal('new_patient')}>+ Novo paciente</Btn>
        </div>
      </div>

      {/* Abas */}
      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:`2px solid ${C.border}` }}>
        {ABAS.map(a => {
          const count = pacientesComDados.filter(p=>(p.situacao||'Em atendimento')===a.label).length;
          const ativo = abaAtiva === a.key;
          return (
            <button key={a.key} onClick={()=>{ setAbaAtiva(a.key); setExpanded(null); }} style={{
              padding:'9px 18px', fontWeight:700, fontSize:'0.85rem',
              background:'none', border:'none', cursor:'pointer',
              color: ativo ? a.color : C.sub,
              borderBottom: ativo ? `3px solid ${a.color}` : '3px solid transparent',
              display:'flex', alignItems:'center', gap:6,
            }}>
              {a.label}
              <span style={{
                background: ativo ? a.color : C.border,
                color: ativo ? '#fff' : C.sub,
                borderRadius:20, padding:'0 7px', fontSize:'0.72rem', fontWeight:800,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Tabela */}
      <div style={{ background:C.white, borderRadius:10, border:`1px solid ${C.border}`, overflow:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:`2px solid ${C.border}`, background:'#f8fafc' }}>
              <th style={{ width:32 }}></th>
              <th style={{ padding:'10px 8px', textAlign:'left', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>PACIENTE</th>
              <th style={{ padding:'10px 8px', textAlign:'left', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>FILIAL</th>
              <th style={{ padding:'10px 8px', textAlign:'left', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>SITUAÇÃO</th>
              <th style={{ padding:'10px 8px', textAlign:'center', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>LESÕES</th>
              <th style={{ padding:'10px 8px', textAlign:'left', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>PRÓXIMA TROCA</th>
              <th style={{ padding:'10px 8px', textAlign:'left', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>META</th>
              <th style={{ padding:'10px 8px', textAlign:'left', fontSize:'0.75rem', fontWeight:700, color:C.sub }}>AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {pacientesFiltrados.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding:'40px', textAlign:'center', color:C.muted, fontSize:'0.9rem' }}>
                  Nenhum paciente nessa situação.
                </td>
              </tr>
            )}
            {pacientesFiltrados.map(pac => (
              <React.Fragment key={pac.id}>
                <PatientRow
                  pac={pac}
                  expanded={expanded === pac.id}
                  onToggle={()=>setExpanded(v=> v===pac.id ? null : pac.id)}
                  onAction={handleAction}
                />
                {expanded === pac.id && (
                  <PatientExpandedPanel pac={pac} onAction={handleAction} />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Modais ── */}

      {/* Novo / editar paciente */}
      {(modal==='new_patient'||modal==='edit_patient') && (
        <Modal title={modal==='new_patient'?'Novo paciente':'Editar paciente'} onClose={()=>setModal(null)} width={600}>
          <Field label="Nome completo" required>
            <AutocompleteNome value={form.nome} onChange={v=>setForm(f=>({...f,nome:v}))} suggestions={nomesSugeridos} />
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <Field label="Data de nasc. (DN)">
              <Inp type="date" value={form.data_nascimento} onChange={v=>setForm(f=>({...f,data_nascimento:v}))} />
            </Field>
            <Field label="Filial">
              <Sel value={form.filial} onChange={v=>setForm(f=>({...f,filial:v}))} options={FILIAIS} placeholder="Selecione..." />
            </Field>
            <Field label="Programa atual">
              <Sel value={form.programa_atual} onChange={v=>setForm(f=>({...f,programa_atual:v}))} options={PROGRAMAS} placeholder="Selecione..." />
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Operadora">
              <Inp value={form.operadora} onChange={v=>setForm(f=>({...f,operadora:v}))} placeholder="Ex: Unimed" />
            </Field>
            <Field label="Data de implantação">
              <Inp type="date" value={form.data_implantacao} onChange={v=>setForm(f=>({...f,data_implantacao:v}))} />
            </Field>
          </div>
          <Field label="Diagnóstico de base">
            <Inp value={form.diagnostico_base} onChange={v=>setForm(f=>({...f,diagnostico_base:v}))} placeholder="Ex: AVC, ICC, DPOC..." />
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Profissional que realizou avaliação">
              <Inp value={form.profissional_avaliacao} onChange={v=>setForm(f=>({...f,profissional_avaliacao:v}))} placeholder="Nome do profissional" />
            </Field>
            <Field label="Origem da lesão">
              <Sel value={form.origem_lesao} onChange={v=>setForm(f=>({...f,origem_lesao:v}))} options={ORIGENS_LESAO} placeholder="Selecione..." />
            </Field>
          </div>
          <Field label="Situação">
            <Sel value={form.situacao} onChange={v=>setForm(f=>({...f,situacao:v}))}
              options={['Em atendimento','Hospitalizado','Alta da comissão','Alta do Homecare']} />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.blue} onClick={save} disabled={saving||!form.nome}>{saving?'Salvando...':'Salvar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Nova lesão / Plano de cuidados */}
      {modal==='new_wound' && (
        <Modal title="Novo plano de cuidados (Lesão)" onClose={()=>setModal(null)} width={600}>
          <Field label="Local, características, dimensões, profundidade" required>
            <Txt
              value={form.descricao_local}
              onChange={v=>setForm(f=>({...f,descricao_local:v}))}
              rows={3}
              placeholder="Ex: Úlcera por pressão sacral estádio II, 3x2cm, profundidade superficial, bordas regulares..."
            />
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Tipo de lesão">
              <Sel value={form.tipo_lesao} onChange={v=>setForm(f=>({...f,tipo_lesao:v}))}
                options={TIPOS_LESAO} placeholder="Selecione..." />
            </Field>
            <Field label="Localização">
              <Sel value={form.localizacao} onChange={v=>setForm(f=>({...f,localizacao:v}))}
                options={LOCALIZACOES} placeholder="Selecione..." />
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <Field label="Estádio">
              <Sel value={form.estadio} onChange={v=>setForm(f=>({...f,estadio:v}))}
                options={ESTADIOS} placeholder="Selecione..." />
            </Field>
            <Field label="Cobertura">
              <Sel value={form.cobertura_atual} onChange={v=>setForm(f=>({...f,cobertura_atual:v}))}
                options={COBERTURAS} placeholder="Selecione..." />
            </Field>
            <Field label="Periodicidade de troca">
              <Sel value={form.periodicidade_troca} onChange={v=>setForm(f=>({...f,periodicidade_troca:v}))}
                options={PERIODICIDADES} placeholder="Selecione..." />
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Meta programada">
              <Inp value={form.meta_programada} onChange={v=>setForm(f=>({...f,meta_programada:v}))}
                placeholder="Ex: Cicatrização em 30 dias" />
            </Field>
            <Field label="Meta atingida?">
              <Sel value={form.meta_status} onChange={v=>setForm(f=>({...f,meta_status:v}))} options={META_OPTS} />
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <Field label="1ª avaliação">
              <Inp type="date" value={form.data_primeira_avaliacao} onChange={v=>setForm(f=>({...f,data_primeira_avaliacao:v}))} />
            </Field>
            <Field label="Data cicatrização">
              <Inp type="date" value={form.data_cicatrizacao_lesao} onChange={v=>setForm(f=>({...f,data_cicatrizacao_lesao:v}))} />
            </Field>
            <Field label="Número de notificação">
              <Inp value={form.numero_notificacao} onChange={v=>setForm(f=>({...f,numero_notificacao:v}))} placeholder="Nº" />
            </Field>
          </div>
          <Field label="Foto(s) da avaliação inicial">
            <FotoUploader
              fotos={form.fotos||[]}
              onChange={fotos => setForm(f=>({...f, fotos}))}
            />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.blue} onClick={save} disabled={saving||!form.descricao_local}>{saving?'Salvando...':'Salvar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Nova evolução */}
      {modal==='evolution' && (
        <Modal title="Nova evolução" onClose={()=>setModal(null)} width={560}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Data" required>
              <Inp type="date" value={form.data} onChange={v=>setForm(f=>({...f,data:v}))} />
            </Field>
            <Field label="Status da evolução">
              <Sel value={form.status_evolucao} onChange={v=>setForm(f=>({...f,status_evolucao:v}))}
                options={EVOLUCAO_STATUS} placeholder="Selecione..." />
            </Field>
          </div>
          <Field label="Cobertura utilizada">
            <Sel value={form.cobertura_utilizada} onChange={v=>setForm(f=>({...f,cobertura_utilizada:v}))}
              options={COBERTURAS} placeholder="Selecione..." />
          </Field>
          <Field label="Profissional">
            <Inp value={form.profissional} onChange={v=>setForm(f=>({...f,profissional:v}))} placeholder="Nome do profissional" />
          </Field>
          <Field label="Observações">
            <Txt value={form.observacoes} onChange={v=>setForm(f=>({...f,observacoes:v}))} rows={3}
              placeholder="Descreva o estado da lesão, intercorrências, etc." />
          </Field>
          <Field label="Fotos da lesão">
            <FotoUploader
              fotos={form.fotos||[]}
              onChange={fotos => setForm(f=>({...f, fotos}))}
            />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.blue} onClick={save} disabled={saving||!form.data}>{saving?'Salvando...':'Registrar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Hospitalizar */}
      {modal==='hospitalize' && (
        <Modal title="Hospitalizar paciente" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>Ao hospitalizar, o paciente será movido para a aba "Hospitalizados".</p>
          <Field label="Data de hospitalização">
            <Inp type="date" value={form.data_hospitalizacao} onChange={v=>setForm(f=>({...f,data_hospitalizacao:v}))} />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.red} onClick={save} disabled={saving}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Desospitalizar */}
      {modal==='desospitalizar' && (
        <Modal title="Desospitalizar paciente" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>O paciente voltará para a aba "Em atendimento".</p>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.amber} onClick={save} disabled={saving}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Cicatrização */}
      {modal==='cicatrizacao' && (
        <Modal title="Registrar cicatrização" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>A lesão será marcada como cicatrizada e a meta como "atingida".</p>
          <Field label="Data de cicatrização">
            <Inp type="date" value={form.data_cicatrizacao} onChange={v=>setForm(f=>({...f,data_cicatrizacao:v}))} />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.green} onClick={save} disabled={saving}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Alta da comissão */}
      {modal==='alta_comissao' && (
        <Modal title="Alta da comissão" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>O paciente será movido para "Alta da comissão". Isso não encerra o cadastro.</p>
          <Field label="Data da alta">
            <Inp type="date" value={form.data_alta} onChange={v=>setForm(f=>({...f,data_alta:v}))} />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.green} onClick={save} disabled={saving}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Alta do Homecare */}
      {modal==='alta_homecare' && (
        <Modal title="Alta do Homecare" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>O paciente encerra o acompanhamento no Homecare (mudança de prestador ou óbito). Isso não exclui o cadastro.</p>
          <Field label="Motivo" required>
            <Sel value={form.motivo_alta_homecare} onChange={v=>setForm(f=>({...f,motivo_alta_homecare:v}))}
              options={MOTIVOS_ALTA_HOMECARE} placeholder="Selecione..." />
          </Field>
          <Field label="Data da alta">
            <Inp type="date" value={form.data_alta} onChange={v=>setForm(f=>({...f,data_alta:v}))} />
          </Field>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.purple} onClick={save} disabled={saving||!form.motivo_alta_homecare}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Reabrir paciente */}
      {modal==='reabrir_pac' && (
        <Modal title="Reativar paciente" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>O paciente voltará para "Em atendimento".</p>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.amber} onClick={save} disabled={saving}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Reabrir lesão */}
      {modal==='reabrir_lesao' && (
        <Modal title="Reabrir lesão" onClose={()=>setModal(null)} width={400}>
          <p style={{ color:C.sub, fontSize:'0.88rem' }}>A lesão voltará a ser acompanhada e a meta será redefinida como "em andamento".</p>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Btn outline color={C.muted} onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn color={C.amber} onClick={save} disabled={saving}>{saving?'Salvando...':'Confirmar'}</Btn>
          </div>
        </Modal>
      )}

      {/* Painel trocas de hoje */}
      {showTrocas && (
        <TrocasHojePanel pacientes={pacientesComDados} onClose={()=>setShowTrocas(false)} />
      )}

      {/* Confirmar exclusão */}
      {delItem && (
        <Modal title="Confirmar exclusão" onClose={()=>setDelItem(null)} width={420}>
          <p style={{ color:C.text, fontSize:'0.88rem', lineHeight:1.5 }}>
            {delItem.tipo==='paciente' && <>Excluir o paciente <strong>{delItem.nome}</strong>? Todas as lesões e evoluções associadas a ele também serão excluídas.</>}
            {delItem.tipo==='lesao' && <>Excluir a lesão <strong>{delItem.nome}</strong>? Todas as evoluções registradas para ela também serão excluídas.</>}
            {delItem.tipo==='evolucao' && <>Excluir a evolução de <strong>{delItem.nome}</strong>?</>}
            <br /><span style={{ color:C.red }}>Esta ação não pode ser desfeita.</span>
          </p>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
            <Btn outline color={C.muted} onClick={()=>setDelItem(null)}>Cancelar</Btn>
            <Btn color={C.red} onClick={confirmarExclusao} disabled={saving}>{saving?'Excluindo...':'Excluir'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
